import { Badge, Button, ConfigProvider, Divider, Modal, Select, Switch, Tabs, TimePicker, Tooltip } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { API_DOMAIN } from '../constants';
import useSnackbar from '../Snackbar/UseSnackbar';
import { DeleteOutlined, EditOutlined, FolderAddOutlined, PlusOutlined } from '@ant-design/icons';
import TemplateAddModal from './Template_add_edit';
import { SettingsTab as ApprovalSettingsTab } from '../approvals/settings/SettingsTab';
import { ChatSettingsPanel } from './ChatSettingsPanel';
import { UpdateNotificationPanel } from './UpdateNotificationPanel';
import { useAuth } from '../contexts/AuthContext';
const settingsOptions = [
    { id: 2, name: "WATI Round-Robin" },
    { id: 3, name: "Query Images Watermark" },
    { id: 4, name: "WATI Quotes Approval" },
    { id: 6, name: "WATI Followup Templates" },
    { id: 7, name: "Approval Settings" },
    { id: 8, name: "Chat Settings" },
    { id: 9, name: "Update Notification" },
];

const WIDE_PANES = new Set([7, 8]);

const SettingsModal = ({ open_settings, handleCloseSettingsModal }) => {

    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar, ToastContainer } = useSnackbar();
    const { authState } = useAuth();

    const [selectedOption, setSelectedOption] = useState(settingsOptions[0].id);
    const [loading, setLoading] = useState(false);
    const [roundRobbinEnabled, setRoundRobbinEnabled] = useState(false);
    const [watermarkEnabled, setWatermarkEnabled] = useState(false);
    const [followupTemplates, setFollowupTemplates] = useState([]);

    const [approvalFeatureToggle, setApprovalFeatureToggle] = useState(false);
    const load_approval_feature_status = async () => {
        try {
            var response = await axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: "template_approval_feature_toggle" });
            if (response.data.success) {
                setApprovalFeatureToggle(response.data.data.on);
            } else {
                WarningSnackbar("Failed to get Approval Feature Info! Try again later\n");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Approval Feature! Try again later\n", error.message);
        }
    };



    const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const [timerange, setTimerange] = useState({ holiday_mode: false, slots: {} });
    const [timerangeSaveLoading, setTimerangeSaveLoading] = useState(false);

    const load_timerange = async () => {
        try {
            const res = await axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: "round_robbin_timerange" });
            if (res.data.success) setTimerange(res.data.data);
        } catch (error) {
            WarningSnackbar("Failed to load time range settings");
        }
    };

    const save_timerange = async () => {
        try {
            setTimerangeSaveLoading(true);
            const res = await axios.post(API_DOMAIN + "/round_robin/update_timerange", { timerange });
            if (res.data.success) SuccessSnackbar("Timerange updated and scheduler(s) refreshed!");
            else ErrorSnackbar("Failed to save time range settings");
        } catch (error) {
            ErrorSnackbar("Failed to save time range settings");
        } finally {
            setTimerangeSaveLoading(false);
        }
    };

    const toggleDayClosed = (day) => {
        setTimerange(prev => ({
            ...prev,
            slots: {
                ...prev.slots,
                [day]: prev.slots[day] === false
                    ? [{ from: { hour: 9, minute: 0 }, to: { hour: 18, minute: 0 } }]
                    : false
            }
        }));
    };

    const addSlot = (day) => {
        setTimerange(prev => ({
            ...prev,
            slots: {
                ...prev.slots,
                [day]: [...(prev.slots[day] || []), { from: { hour: 9, minute: 0 }, to: { hour: 18, minute: 0 } }]
            }
        }));
    };

    const removeSlot = (day, index) => {
        setTimerange(prev => ({
            ...prev,
            slots: {
                ...prev.slots,
                [day]: prev.slots[day].filter((_, i) => i !== index)
            }
        }));
    };

    const updateSlotTime = (day, slotIndex, which, dayjsVal) => {
        if (!dayjsVal) return;
        setTimerange(prev => ({
            ...prev,
            slots: {
                ...prev.slots,
                [day]: prev.slots[day].map((slot, i) =>
                    i === slotIndex ? { ...slot, [which]: { hour: dayjsVal.hour(), minute: dayjsVal.minute() } } : slot
                )
            }
        }));
    };

    const [assignableUsers, setAssignableUsers] = useState([]);
    const load_assignable_users = async () => {
        try {
            var response = await axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: "wati_operators_online_status" });
            if (response.data.success) {
                setAssignableUsers(response.data.data);
            } else {
                WarningSnackbar("Failed to get Operators Info! Try again later\n");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Operators Info! Try again later\n", error.message);
        }
    };

    const [wati_approval_options, setWati_approval_options] = useState([]);
    const load_approval_options = async () => {
        try {
            var response = await axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: "wati_message_forward_approval" });
            if (response.data.success) {
                setWati_approval_options(response.data.data);
            } else {
                WarningSnackbar("Failed to get Operators Info! Try again later\n");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Operators Info! Try again later\n", error.message);
        }
    };

    const [users, setUsers] = useState([]);
    const load_all_users = async () => {
        try {
            var response = await axios.get(API_DOMAIN + "/auth/get_users_for_settings");
            if (response.data.success) {
                setUsers(response.data.data);
            } else {
                WarningSnackbar("Failed to get Operators Info! Try again later\n");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Operators Info! Try again later\n", error.message);
        }
    };

    const load_auto_function_status = async () => {
        try {
            var response = await axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: "round_robbin_active" });
            if (response.data.success) {
                setRoundRobbinEnabled(response?.data?.data?.active || false);
            } else {
                WarningSnackbar("Failed to get Auto-Assigning function status");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Auto-Assigning function status! Try again later\n", error.message);
        }
    };

    const load_wati_followup_templates = async () => {
        try {
            var response = await axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: "wati_followup_templates" });
            if (response.data.success) {
                setFollowupTemplates(response?.data?.data || false);
            } else {
                WarningSnackbar("Failed to get Followup templates");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Followup templates! Try again later\n", error.message);
        }
    };

    const [templateAddEditModalData, setTemplateAddEditModalData] = useState({ index: null, followupTemplates, setFollowupTemplates, addModalOpen: false });

    const toggle_operator_assignable = (id, checkValue) => {
        setAssignableUsers(prevUsers =>
            prevUsers.map(user =>
                user.id === id ? { ...user, assignable: checkValue } : user
            )
        );
    };


    useEffect(() => {
        if (open_settings) {
            load_assignable_users();
            load_auto_function_status();
            load_approval_options();
            load_all_users();
            load_approval_feature_status();
            load_wati_followup_templates();
            load_timerange();
        }
    }, [open_settings])

    const handleMenuClick = (id) => {
        setSelectedOption(id);
    };

    const handleRoundRobbinToggle = (e) => {
        setRoundRobbinEnabled(e)
    };
    const handleWatermarkToggle = (e) => {
        setWatermarkEnabled(e)
    };
    const handleApprovalFeatureToggle = (e) => {
        setApprovalFeatureToggle(e)
    };

    //final save
    const save_settings = async () => {
        setLoading(true)
        try {
            const results = await Promise.all([
                save_operator_assignability(),
                save_user_wati_approval_info(),
                save_toggle_roundrobbin(),
                save_toggle_wati_approval(),
                save_wati_templates(),
            ]);
            if (results.every(Boolean)) {
                handleCloseSettingsModal();
                SuccessSnackbar("Saved Settings Successfully!")
            } else {
                ErrorSnackbar("Some settings failed to save — check the errors above and try again.")
            }
        } catch (error) {
            ErrorSnackbar("Saving settings failed!")
        }
        finally {
            setLoading(false)
        }
    }

    const handleSave = () => {
        if (window.confirm("Are you sure to save changes you made in the settings?")) {
            save_settings()
        }
    };

    const save_operator_assignability = async () => {
        try {
            const res = await axios.post(API_DOMAIN + "/whatsapp/save_misc_mode", { mode: "wati_operators_online_status", moredetails: assignableUsers });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message)
            return false;
        }
    }

    const save_toggle_roundrobbin = async () => {
        try {
            const res = await axios.post(API_DOMAIN + "/round_robin/toggle", { checked: roundRobbinEnabled });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message)
            return false;
        }
    }

    const save_toggle_wati_approval = async () => {
        try {
            const res = await axios.post(API_DOMAIN + "/whatsapp/save_misc_mode", { mode: "template_approval_feature_toggle", moredetails: { on: approvalFeatureToggle } });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message)
            return false;
        }
    }

    const save_wati_templates = async () => {
        try {
            const res = await axios.post(API_DOMAIN + "/whatsapp/save_misc_mode", { mode: "wati_followup_templates", moredetails: followupTemplates });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message)
            return false;
        }
    }

    const change_wati_msg_approval_status = (value, id) => {
        setUsers(prevUsers =>
            prevUsers.map(user =>
                user.id === id ? { ...user, wati_msg_approval: value } : user
            )
        );
    }

    const save_user_wati_approval_info = async () => {
        try {
            const res = await axios.post(API_DOMAIN + "/auth/save_users_for_settings", { users: users });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message)
            return false;
        }
    }

    const delete_wati_template = (index) => {
        setFollowupTemplates((prev) =>
            prev.filter((_, i) => i !== index)
        )
    }

    const edit_wati_template = (index) => {
        setTemplateAddEditModalData({
            index,
            followupTemplates,
            setFollowupTemplates,
            addModalOpen: true
        })
    }

    const openAddTemplate = () => {
        setTemplateAddEditModalData({
            index: null,
            followupTemplates,
            setFollowupTemplates,
            addModalOpen: true
        })
    }

    const renderDetails = () => {
        switch (selectedOption) {
            case 2:
                return (
                    <div className='p-2 drop-shadow'>
                        <h5>WATI Automated Round-Robin Ticket Assignment</h5>
                        <Divider type='horizontal' className='p-0 m-0 mt-1 mb-2' style={{ backgroundColor: "black" }} />
                        <Tabs
                            size="small"
                            items={[
                                {
                                    key: 'operators',
                                    label: 'Operators',
                                    children: (
                                        <div>
                                            <div className='border rounded p-2 flex justify-between' style={{ backgroundColor: "" }}>
                                                <div>Toggle Auto-Assign On/Off</div>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Switch: {
                                                                handleBg: "#14b840",
                                                                colorPrimary: "#63db83",
                                                                colorPrimaryHover: "#408f55",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Switch
                                                        checkedChildren="On"
                                                        unCheckedChildren="Off"
                                                        value={roundRobbinEnabled}
                                                        onChange={handleRoundRobbinToggle}
                                                    />
                                                </ConfigProvider>
                                            </div>
                                            <div className='mt-2 p-1'>Set Assignability of Operators</div>
                                            <Divider type='horizontal' className='p-0 m-0' style={{ backgroundColor: "black" }} />
                                            <div className='border rounded mt-2 p-2 overflow-y-auto' style={{ height: "230px" }}>
                                                <div className='mt-1'>
                                                    {assignableUsers?.length ? (
                                                        assignableUsers.map((element, index) => (
                                                            <div className='rounded border p-2 m-2 flex justify-between' key={index}>
                                                                <div>
                                                                    <div>{element.fullName}</div>
                                                                    <div style={{ fontSize: "12px", color: "gray" }}>
                                                                        {element.email ? element.email : (<i>&lt;no email id available&gt;</i>)}
                                                                    </div>
                                                                    <div style={{ fontSize: "12px", color: "gray" }}>
                                                                        {element?.roles.length > 0 ? element.roles.join(", ") : (<i>&lt;roles data not available&gt;</i>)}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    {element?.email ? (
                                                                        <Switch
                                                                            checkedChildren="On"
                                                                            unCheckedChildren="Off"
                                                                            value={element.assignable}
                                                                            onChange={(e) => toggle_operator_assignable(element.id, e)}
                                                                        />
                                                                    ) : (
                                                                        <Tooltip placement="left" title="Bots are system managed, Can't assign to them directly.">
                                                                            <Switch checkedChildren="On" unCheckedChildren="Off" value={false} disabled />
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className='flex justify-center pt-1'>
                                                <i style={{ color: "gray", fontSize: "10px" }}>Total agents including Bots : {assignableUsers.length}</i>
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'timings',
                                    label: 'Timings',
                                    children: (
                                        <div>
                                            <div className='border rounded p-2 flex justify-between mb-2'>
                                                <div>Holiday Mode <i style={{ fontSize: "11px", color: "gray" }}>(disables all auto-assignments)</i></div>
                                                <Switch
                                                    checkedChildren="Holiday"
                                                    unCheckedChildren="Active"
                                                    checked={timerange.holiday_mode}
                                                    onChange={(val) => setTimerange(prev => ({ ...prev, holiday_mode: val }))}
                                                />
                                            </div>
                                            <div className='border rounded p-1 overflow-y-auto' style={{ height: "255px" }}>
                                                {DAYS.map(day => {
                                                    const isOpen = timerange.slots[day] !== false;
                                                    const slots = isOpen ? (timerange.slots[day] || []) : [];
                                                    return (
                                                        <div key={day} className='border rounded p-2 mb-1'>
                                                            <div className='flex justify-between items-center mb-1'>
                                                                <span style={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 80 }}>{day}</span>
                                                                <Switch
                                                                    size="small"
                                                                    checkedChildren="Open"
                                                                    unCheckedChildren="Closed"
                                                                    checked={isOpen}
                                                                    onChange={() => toggleDayClosed(day)}
                                                                />
                                                            </div>
                                                            {isOpen && (
                                                                <div>
                                                                    {slots.map((slot, idx) => (
                                                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                                            <TimePicker
                                                                                size="small"
                                                                                format="HH:mm"
                                                                                minuteStep={15}
                                                                                value={dayjs().hour(slot.from.hour).minute(slot.from.minute).second(0)}
                                                                                onChange={(val) => updateSlotTime(day, idx, 'from', val)}
                                                                                allowClear={false}
                                                                            />
                                                                            <span style={{ fontSize: 11, color: "gray" }}>to</span>
                                                                            <TimePicker
                                                                                size="small"
                                                                                format="HH:mm"
                                                                                minuteStep={15}
                                                                                value={dayjs().hour(slot.to.hour).minute(slot.to.minute).second(0)}
                                                                                onChange={(val) => updateSlotTime(day, idx, 'to', val)}
                                                                                allowClear={false}
                                                                            />
                                                                            <DeleteOutlined
                                                                                style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                                                                onClick={() => removeSlot(day, idx)}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                    <Button
                                                                        size="small"
                                                                        type="dashed"
                                                                        icon={<PlusOutlined />}
                                                                        onClick={() => addSlot(day)}
                                                                    >
                                                                        Add Slot
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className='mt-2 flex justify-end'>
                                                <Button size="small" type="primary" loading={timerangeSaveLoading} onClick={save_timerange}>
                                                    Save Timings
                                                </Button>
                                            </div>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                )
            case 3:
                return (
                    <div className='p-2 drop-shadow'>
                        <h5>Watermark Settings</h5>
                        <Divider type='horizontal' className='p-0 m-0 mt-1 mb-2' style={{ backgroundColor: "black" }} />

                        <div className='border rounded p-2'>
                            <div className='flex justify-between mb-2'>
                                <div>Set Photo Watermark</div>
                                <Tooltip title="Turn the feature on/off">
                                    <Switch
                                        checkedChildren="On"
                                        unCheckedChildren="Off"
                                        value={watermarkEnabled}
                                        onChange={handleWatermarkToggle}
                                    />
                                </Tooltip>
                            </div>
                            <Divider type='horizontal' className='p-0 m-0' style={{ backgroundColor: "black" }} />

                        </div>
                    </div>)
            case 4:
                return (<div className='p-2 drop-shadow'>
                    <h5>Set Who needs approval and who can approve</h5>
                    <Divider type='horizontal' className='p-0 m-0 mt-1 mb-3' style={{ backgroundColor: "black" }} />

                    <div className='border flex justify-between rounded p-2 mb-2'>
                        <div>
                            Set the feature ON or OFF
                        </div>
                        <div>
                            <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                value={approvalFeatureToggle}
                                onChange={handleApprovalFeatureToggle}
                            />
                        </div>
                    </div>

                    <div className='border rounded p-2' style={{ height: "330px", overflowY: "auto" }}>
                        {users?.length > 0 ?
                            users.map((element) => {
                                return (
                                    <div className='border rounded p-2 flex justify-between mb-2'>
                                        <div>
                                            <div>
                                                {element.name}
                                            </div>
                                            <div>
                                                <i style={{ fontSize: "12px", color: "gray" }}>{element.email}</i>
                                            </div>
                                            <div>
                                                <i style={{ fontSize: "12px", color: "gray" }}>Admin :&nbsp;{element.admin ? "Yes" : "No"}</i>
                                            </div>
                                        </div>
                                        <div>
                                            <Select
                                                optionFilterProp="label"
                                                style={{ width: '100%' }}
                                                placeholder="Select Ability"
                                                disabled={!approvalFeatureToggle}
                                                value={element.wati_msg_approval}
                                                onChange={(e) => change_wati_msg_approval_status(e, element.id)}
                                                options={wati_approval_options}
                                            />
                                        </div>
                                    </div>
                                )
                            })
                            : (
                                <div className='border rounded p-2 flex justify-between mb-2'>
                                    <i>No Users Found!!</i>
                                </div>
                            )}
                    </div>
                </div>)
            case 6:
                return (<div className='p-2 drop-shadow'>
                    <div className='flex justify-between'>
                        <h5>Modify WATI Followup Templates</h5>
                        <Tooltip title="Add new Template">
                            <Button style={{ width: "60px" }} onClick={() => openAddTemplate()} type="primary" className='hover:scale-110'>
                                <FolderAddOutlined />
                            </Button>
                        </Tooltip>
                    </div>
                    <Divider type='horizontal' className='p-0 m-0 mt-1 mb-1.5' style={{ backgroundColor: "black" }} />

                    <div className='flex justify-center'>
                        Total&nbsp;<b>{followupTemplates?.length || 0}</b>&nbsp;Templates
                    </div>
                    <div className='border rounded p-2' style={{ height: "310px", overflowY: "auto" }}>
                        {followupTemplates?.length > 0 ?
                            followupTemplates.map((element, index) => {
                                return (
                                    <div>
                                        <div className='border rounded p-2 flex justify-between mb-2'>
                                            <div className='p-2'>
                                                <div className='flex justify-center'>
                                                    <Badge overflowCount={9999999999} showZero count={index + 1} color="blue" status="progress" className='mb-3' />
                                                </div>

                                                <div className='border rounded p-2'>
                                                    <EditOutlined className='mb-3 hover:cursor-pointer hover:scale-110' onClick={() => edit_wati_template(index)} />

                                                    <DeleteOutlined className='hover:cursor-pointer hover:scale-110' onClick={() => window.confirm(`Are you sure to delete this template?`) ? delete_wati_template(index) : null} />
                                                </div>

                                            </div>
                                            <div className='border rounded m-2 p-2' style={{ overflowY: "auto", maxWidth: "360px", height: "150px" }}>
                                                {element}
                                            </div>
                                            {/* actions */}
                                        </div>
                                    </div>
                                )
                            })
                            : (
                                <div className='border rounded p-2 flex justify-between mb-2'>
                                    <i>No templates Found!!</i>
                                </div>
                            )}
                    </div>
                </div>)
            case 7:
                return (<div className='p-2 drop-shadow'>
                    <h5>Approval Settings</h5>
                    <Divider type='horizontal' className='p-0 m-0 mt-1 mb-2' style={{ backgroundColor: "black" }} />
                    <ApprovalSettingsTab users={users} />
                </div>)
            case 8:
                return (<div className='p-2 drop-shadow'>
                    <h5>Chat Settings</h5>
                    <Divider type='horizontal' className='p-0 m-0 mt-1 mb-2' style={{ backgroundColor: "black" }} />
                    <ChatSettingsPanel authState={authState} />
                </div>)
            case 9:
                return (<div className='p-2 drop-shadow'>
                    <h5>Update Notification</h5>
                    <Divider type='horizontal' className='p-0 m-0 mt-1 mb-2' style={{ backgroundColor: "black" }} />
                    <UpdateNotificationPanel />
                </div>)
            default:
                return <div>Keep calm, coffee is being converted to code...</div>;
        }
    };

    return (
        <>
            <Modal
                title="Settings"
                centered
                open={open_settings}
                onCancel={handleCloseSettingsModal}
                maskClosable={false}
                width={WIDE_PANES.has(selectedOption) ? 1100 : 800}
                footer={
                    <div className="flex justify-end">
                        <Button loading={loading} type="primary" onClick={handleSave}>
                            Save Changes
                        </Button>
                    </div>}
            >
                <div className='border rounded p-2 drop-shadow' style={{ backgroundColor: "#f7f7f7" }}>
                    <div className='drop-shadow flex gap-3' style={{ display: 'flex', height: '460px' }}>
                        {/* Menu Pane */}
                        <div className='border rounded p-2' style={{ width: '200px', backgroundColor: "#fefefe" }}>
                            <div className='flex justify-center'>Menu Items</div>
                            <Divider type='horizontal' className='p-0 m-0 mt-1 mb-2' style={{ backgroundColor: "black" }} />
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {settingsOptions.map(option => (
                                    <li key={option.id}
                                        style={{
                                            padding: '10px',
                                            borderRadius: "8px",
                                            cursor: 'pointer',
                                            backgroundColor: selectedOption === option.id ? '#404040' : 'transparent',
                                            transition: "background-color 0.3s, color 0.3s ease-in-out",
                                            color: selectedOption === option.id ? 'white' : 'black',
                                        }}
                                        onClick={() => handleMenuClick(option.id)}>
                                        {option.name}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='m-0' style={{ display: "flex", alignItems: "center", height: "100%" }}>
                            <Divider type='vertical' style={{ backgroundColor: "black", height: "95%" }} />
                        </div>


                        {/* Details Pane */}
                        <div className='border rounded p-2' style={{ backgroundColor: "#fbfbfb", width: WIDE_PANES.has(selectedOption) ? "790px" : "490px", overflowY: "auto" }}>
                            {renderDetails()}
                        </div>
                    </div>
                </div>
            </Modal>

            <TemplateAddModal data={templateAddEditModalData} dataSetter={setTemplateAddEditModalData} />

        </>
    );
};

export default SettingsModal;
